/**
 * Notification funnel rules (FUN-02, CAMP-03): the rate of each passage
 * (sent → delivered → clicks → orders) and the comparison of a campaign's
 * number with the store average.
 */
import { roundTo1 } from '@/lib/period-comparison';

export interface FunnelStages {
  sent: number;
  delivered: number;
  clicks: number;
  orders: number;
}

/** Fewest sent campaigns in the window for the average to mean something. */
const MIN_CAMPAIGNS_FOR_AVERAGE = 2;

function rate(part: number, total: number): number | null {
  return total === 0 ? null : roundTo1((part / total) * 100);
}

/**
 * delivered ÷ sent, clicks ÷ delivered, orders ÷ clicks, in % with 1 decimal;
 * `null` ("—") when the denominator is 0.
 */
export function funnelSteps(
  stages: FunnelStages
): Array<{ rate: number | null }> {
  return [
    { rate: rate(stages.delivered, stages.sent) },
    { rate: rate(stages.clicks, stages.delivered) },
    { rate: rate(stages.orders, stages.clicks) },
  ];
}

/**
 * `above` (≥ average) or `below`; `none` with fewer than 2 campaigns in the
 * window, without an average (failed) or without a value (hidden data).
 */
// SPEC_DEVIATION: takes the number of campaigns in the window instead of
// `enoughCampaigns: boolean`, and `value` may be `null`. Reason: keeps the
// "2+ campaigns" rule of CAMP-03 here instead of in every caller.
export function compareToAverage(
  value: number | null,
  average: number | null,
  campaignsInWindow: number
): { kind: 'above' | 'below' | 'none' } {
  if (
    value === null ||
    average === null ||
    campaignsInWindow < MIN_CAMPAIGNS_FOR_AVERAGE
  ) {
    return { kind: 'none' };
  }
  return { kind: value >= average ? 'above' : 'below' };
}
